import { BaseSerializer } from '@core/serializers/baseSerializer';

class CartSerializer extends BaseSerializer {
  // Get userId from localStorage or user store
  getUserId() {
    // 1. Try authenticated user from the user store (first priority)
    const userStore = window.$stores?.user;
    const currentUser = userStore?.state?.current;
    if (currentUser) {
      const id = currentUser.id || currentUser._id;
      if (id) return id;
    }

    // 2. Fallback to persistent localStorage key
    const storedId = localStorage.getItem('user_id');
    if (storedId) return storedId;

    // 3. Generate a stable temp ID for guest sessions
    const tempUserId = 'temp-user-' + Date.now();
    localStorage.setItem('user_id', tempUserId);
    return tempUserId;
  }

  // Serialize cart item for API requests
  serialize(data) {
    console.log('🚀 ~ CartSerializer ~ serialize ~ data:', data);
    if (!data) return {};

    // For adding items to cart - API expects flat structure with userId and a nested product object
    if (data.dishId || data.item) {
      const item = data.item || data;
      return {
        userId: data.userId || this.getUserId(),
        restaurantId: data.restaurantId || item.storeId,
        restaurantName:
          data.restaurantName?.name || item.storeName?.name || 'Restaurant',
        product: {
          id: data.dishId || item.dishId || item.id,
          name: data.name || item.name || item.title,
          price: data.price || item.price || 0,
          image:
            data.principalImageUrl ||
            item.principalImageUrl ||
            item.image ||
            '',
          fullOptions: data.fullOptions || [],
          fullAccompaniments: data.fullAccompaniments || []
        },
        quantity: data.quantity || 1,
        options: (data.options || []).map((opt) => {
          return {
            id: `${opt?.optionId}---${opt?.valueId}`, // This is the combined ID
            name: `${opt?.optionName} • ${opt?.valueName}`,
            price: parseInt(opt?.price || 0, 10),
            quantity: opt?.quantity
          };
        }),
        accompaniments: data.accompaniments || []
      };
    }

    // For updating item quantity
    if (data.quantity !== undefined) {
      return {
        quantity: data.quantity
      };
    }

    return { ...data, userId: data.userId || this.getUserId() };
  }

  // Deserialize API responses to app format
  deserializeCouponValidate(raw) {
    console.log('[Cart Serializer] deserializeCouponValidate received:', raw);
    const serverRes = raw.error || raw;
    // Check for both statusCode === 200 and success === true
    const success =
      serverRes.statusCode === 200 ||
      raw.status === 200 ||
      serverRes.success === true;
    console.log(
      '[Cart Serializer] deserializeCouponValidate:',
      success,
      'serverRes:',
      serverRes
    );

    if (!success) {
      let message = serverRes.message || 'Erreur lors de la requête';
      let titleError = '';
      if (serverRes.statusCode === 404) {
        message = 'Coupon invalide ou expiré';
        titleError = 'Coupon invalide ou expiré';
      }
      if (serverRes.statusCode === 409) {
        message = 'Coupon déjà appliqué';
        titleError = 'Coupon déjà appliqué';
      }
      if (
        serverRes.statusCode === 400 &&
        /(invalid amount)|(minimum order amount not reached)/i.test(
          serverRes.message
        )
      ) {
        titleError = 'Panier insuffisant';
        message =
          'Le montant du panier est insuffisant pour appliquer ce coupon';
      }

      return {
        statusCode: serverRes.statusCode || raw.status || 500,
        success: false,
        message: message,
        titleError: titleError,
        data: serverRes.error || serverRes.data || null
      };
    }

    return {
      statusCode: 200,
      success: true,
      message: 'OK',
      data: serverRes?.data
    };
  }

  // Deserialize API responses to app format
  deserialize(raw) {
    console.log('[Cart Serializer] deserialize received:', raw);
    const serverRes = raw.data || raw.error;
    // Check for both statusCode === 200 and success === true
    const success =
      serverRes.statusCode === 200 ||
      raw.status === 200 ||
      serverRes.success === true;
    console.log('[Cart Serializer] success:', success, 'serverRes:', serverRes);

    if (!success) {
      if (
        serverRes.statusCode === 404 ||
        raw.status === 404 ||
        serverRes.error?.statusCode === 404
      ) {
        console.log('[Cart Serializer] 404 detected, returning empty cart');
        return {
          statusCode: 200,
          success: true,
          message: 'OK',
          data: null
        };
      }
      if (
        serverRes.statusCode === 409 ||
        raw.status === 409 ||
        serverRes.error?.statusCode === 409
      ) {
        console.log('[Cart Serializer] 409 detected, returning empty cart');
        return {
          statusCode: 409,
          success: false,
          message: 'OK',
          data: { code: 'DIFFERENT_RESTAURANT' }
        };
      }
      return {
        statusCode: serverRes.statusCode || raw.status || 500,
        success: false,
        message: serverRes.message || 'Erreur lors de la requête',
        data: serverRes.error || serverRes.data || null
      };
    }

    // Extract cart data from response
    // API returns cart directly in data: { success: true, data: {...cart} }
    const cartData = serverRes.data;
    console.log('[Cart Serializer] cartData:', cartData);

    if (!cartData) {
      console.log('[Cart Serializer] No cartData, returning null');
      return {
        statusCode: 200,
        success: true,
        message: 'OK',
        data: null
      };
    }

    const serialized = this.deserializeCart(cartData);
    console.log('[Cart Serializer] serialized cart:', serialized);

    return {
      statusCode: 200,
      success: true,
      message: 'OK',
      data: serialized
    };
  }

  deserializeCart(cart) {
    return {
      id: cart._id || cart.id,
      userId: cart.userId,
      restaurantId: cart.restaurantId,
      restaurantName: cart.restaurantName,
      items: (cart.orderedItems || cart.items || []).map((item) =>
        this.deserializeCartItem(item)
      ),
      drinks: (cart.drinks || []).map((drink) => ({
        cartDrinkId: drink.id,
        drinkId: drink.drink?.id || drink.drinkId || drink.id,
        name: drink.drink?.name || drink.name || '',
        quantity: drink.quantity || 1,
        price: drink.drink?.price || drink.unitPrice || drink.price || 0,
        total:
          drink.total ||
          (drink.unitPrice || drink.price || 0) * (drink.quantity || 1)
      })),
      drinksTotal: cart.drinksTotal || 0,
      subtotal: cart.subtotal || 0,
      deliveryFee: cart.deliveryFee || 0,
      originalDeliveryFee: cart.originalDeliveryFee || cart.deliveryFee || 0,
      deliveryPromotion: cart.deliveryPromotion || null,
      serviceFee: cart.serviceFee || 0,
      discount: cart.discount || 0,
      total: cart.total || 0,
      coupon: cart.coupon || null,
      deliveryAddress: cart.deliveryAddress || null,
      createdAt: cart.createdAt || new Date().toISOString(),
      updatedAt: cart.updatedAt || new Date().toISOString()
    };
  }

  deserializeCartItem(item) {
    const product = item.product || {};
    const options = (item.options || []).map((opt) => {
      const fullName = opt.name || opt.optionName || '';
      const fullId = opt.option?.id || opt.optionId || opt.id || '';
      const [optionName, valueName] = fullName.split(' • ');
      const [optionId, valueId] = fullId.split('---');
      return {
        cartOptionId: opt.id,
        optionId: optionId || fullId,
        optionName: optionName || '',
        valueId: valueId || null,
        valueName: valueName || '',
        price: opt.price || 0,
        quantity: opt.quantity || 1,
        total: opt.total || (opt.price || 0) * (opt.quantity || 1)
      };
    });
    return {
      id: item.cartItemId || item._id || item.id,
      dishId: product.id || item.dishId,
      title: product.name || item.title || item.name,
      name: product.name || item.name || item.title,
      dishName: product.name || item.dishName || item.name,
      dishImage:
        product.image || item.dishImage || item.principalImageUrl || '',
      quantity: item.quantity || 1,
      unitPrice: item.unitPrice || item.price || product.price || 0,
      price: item.price || product.price || 0,
      storeId: item.storeId,
      storeName: item.storeName,
      currency: item.currency,
      description: item.description,
      category: item.category,
      categoryId: item.categoryId,
      principalImageUrl: product.image || item.principalImageUrl,
      options: options,
      accompaniments: (item.accompaniments || []).map((acc) => ({
        cartAccId: acc.id,
        id: acc.accompaniment?.id || acc.id || acc.accompanimentId,
        name: acc.accompaniment?.name || acc.name || '',
        price: acc.accompaniment?.price || acc.price || 0,
        quantity: acc.quantity || 1,
        total: acc.total || (acc.price || 0) * (acc.quantity || 1)
      })),
      drinks: (item.drinks || []).map((drink) => ({
        cartDrinkId: drink.id,
        drinkId: drink.drink?.id || drink.drinkId || drink.id,
        name: drink.drink?.name || drink.name || '',
        quantity: drink.quantity || 1,
        price: drink.drink?.price || drink.unitPrice || drink.price || 0,
        total:
          drink.total ||
          (drink.unitPrice || drink.price || 0) * (drink.quantity || 1)
      })),
      fullOptions: product.fullOptions || null,
      fullDrinks: product.fullDrinks || null,
      fullAccompaniments: product.fullAccompaniments || null,
      totalPrice: item.total || item.totalPrice || 0,
      promotion: item.promotion || null,
      originalPrice: item.originalPrice || null
    };
  }

  serializeFeesCalculation(data) {
    if (!data) return {};

    const deliveryLocation = data.deliveryLocation || {};

    return {
      restaurantId: data.restaurantId,
      distanceKm: data.distanceKm || 0,
      durationMin: data.durationMin || 0,
      items: (data.cartItems || []).map((item) => ({
        dishId: item.dishId || item.id,
        title: item.title || item.name,
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || item.price || 0,
        totalPrice:
          item.totalPrice ||
          (item.unitPrice || item.price || 0) * (item.quantity || 1)
      })),
      deliveryAddress: {
        lat: deliveryLocation.lat || deliveryLocation.coordinates?.lat,
        lng: deliveryLocation.lng || deliveryLocation.coordinates?.lng,
        address: deliveryLocation.address,
        city: deliveryLocation.city,
        description: deliveryLocation.description || '',
        contactName: deliveryLocation.contactName || '',
        contactPhone: deliveryLocation.contactPhone || ''
      },
      deliveryOption: data.deliveryOption || 'delivery'
    };
  }

  // Deserialize fees calculation response
  deserializeFeesCalculation(raw) {
    console.log('[Cart Serializer] deserializeFeesCalculation received:', raw);
    const serverRes = raw.data || raw.error;
    const success =
      serverRes.statusCode === 200 ||
      raw.status === 200 ||
      serverRes.success === true;
    console.log('[Cart Serializer] fees calculation success:', success);

    if (!success) {
      return {
        statusCode: serverRes.statusCode || raw.status || 500,
        success: false,
        message: serverRes.message || 'Erreur lors du calcul des frais',
        code: serverRes.code || 'FEES_CALCULATION_ERROR',
        data: serverRes.error || serverRes.data || null
      };
    }

    // Extract fees data from response
    const feesData = serverRes.data;
    console.log('[Cart Serializer] feesData:', feesData);

    if (!feesData) {
      return {
        statusCode: 200,
        success: true,
        message: 'OK',
        data: null
      };
    }

    return {
      statusCode: 200,
      success: true,
      message: 'OK',
      data: feesData
    };
  }
}

export const serializer = new CartSerializer();
